import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { ArrowClockwise, Download, ShareNetwork, Ticket } from '@phosphor-icons/react';
import { FaFacebookF, FaTelegram, FaWhatsapp, FaXTwitter } from 'react-icons/fa6';

import { API, BACKEND_URL } from '../context/AuthContext';

const BRAND = {
  background: '#081c24',
  panel: '#113945',
  panelSoft: '#173f49',
  accent: '#9f1d1e',
  accentHover: '#b62527',
  gold: '#c4a768',
  text: '#f6f2e8',
  muted: '#b7c5c5',
  success: '#dff0cf',
};

const previewableFieldTypes = new Set(['text', 'textarea', 'email', 'phone', 'date', 'number', 'dropdown']);

const PUBLIC_STATUS_LABELS = {
  pending_approval: 'Onay bekliyor',
  generated: 'Hazır',
  render_failed: 'Render hatası',
  new: 'Yeni',
  ticket_generated: 'Bilet oluşturuldu',
  ticket_sent: 'Bilet gönderildi',
};

const FALLBACK_TRANSLATIONS = {
  de: {
    create_ticket: 'Biletimi Oluştur',
    preview_ticket: 'Vorschau zeigen',
    success_title: 'Dein Ticket ist fertig',
    success_text: 'Dein persönliches Hatıra Bileti wurde erfolgreich erstellt.',
    download_ticket: 'Bileti İndir',
    share_ticket: 'Bileti teilen',
    reset_form: 'Yeni Bilet Oluştur',
    preview_title: 'Ticket-Vorschau',
    result_title: 'Ergebnis',
    responsible_label: 'Verantwortlich',
    form_title: 'Deine Daten',
    form_subtitle: 'Erstelle dein persönliches Erinnerungs-Ticket',
  },
  tr: {
    create_ticket: 'Biletimi Oluştur',
    preview_ticket: 'Önizlemeyi Göster',
    success_title: 'Biletiniz Hazır!',
    success_text: 'Kişiselleştirilmiş Hatıra Biletiniz oluşturuldu.',
    download_ticket: 'Bileti İndir',
    share_ticket: 'Bileti Paylaş',
    reset_form: 'Yeni Bilet Oluştur',
    preview_title: 'Bilet Önizlemesi',
    result_title: 'Sonuç',
    responsible_label: 'Sorumlu Kurum',
    form_title: 'Bilgilerinizi Girin',
    form_subtitle: 'Kişiselleştirilmiş hatıra biletinizi oluşturun',
  },
};

const INPUT_HINTS = {
  tr: {
    email: 'E-posta adresinizi girin',
    phone: 'Telefon numaranızı girin',
    number: 'Bilginizi girin',
  },
  de: {
    email: 'E-Mail-Adresse eingeben',
    phone: 'Telefonnummer eingeben',
    number: 'Wert eingeben',
  },
};

const socialButtonBase = 'inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-white transition-transform duration-200 hover:-translate-y-0.5';

export function PublicCampaignPage() {
  const { slug } = useParams();
  const resultRef = useRef(null);
  const formRef = useRef(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({});
  const [consents, setConsents] = useState({});
  const [previewTicket, setPreviewTicket] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedState, setSubmittedState] = useState(null);
  const [selectedLocale, setSelectedLocale] = useState('tr');
  const [shareMessage, setShareMessage] = useState('');

  const resolveInitialLocale = (localeSettings) => {
    const supported = localeSettings?.supported_locales || ['tr', 'de'];
    if (supported.includes('tr')) return 'tr';
    return localeSettings?.default_locale || supported[0] || 'tr';
  };

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const { data } = await axios.get(`${API}/public/campaign/${slug}`);
        setCampaign(data);

        const initialData = {};
        data.form_fields?.forEach((field) => {
          initialData[field.id] = field.default_value || (field.type === 'checkbox' || field.type === 'consent' ? false : '');
        });
        setFormData(initialData);

        const initialConsents = {};
        Object.entries(data.consent_settings || {}).forEach(([key, config]) => {
          if (config && typeof config === 'object' && config.enabled !== undefined) initialConsents[key] = false;
        });
        setConsents(initialConsents);
        setSelectedLocale(resolveInitialLocale(data.locale_settings));
      } catch (err) {
        setError(err.response?.data?.detail || 'Kampanya bulunamadı');
      } finally {
        setLoading(false);
      }
    };
    fetchCampaign();
  }, [slug]);

  useEffect(() => {
    if (submittedState && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [submittedState]);

  const settings = campaign?.page_settings || {};
  const canPreview = Boolean(campaign?.generate_ticket && campaign?.template);
  const translations = campaign?.locale_settings?.translations || {};
  const supportedLocales = campaign?.locale_settings?.supported_locales || ['tr', 'de'];
  const legalInfo = campaign?.legal_info || {};
  const consentSettings = campaign?.consent_settings || {};
  const t = (key) => translations?.[selectedLocale]?.[key] || FALLBACK_TRANSLATIONS[selectedLocale]?.[key] || FALLBACK_TRANSLATIONS.tr[key] || key;

  const renderedFields = useMemo(() => campaign?.form_fields || [], [campaign]);
 // const publicTicketAbsoluteUrl = submittedState?.ticket_public_url ? new URL(submittedState.ticket_public_url, BACKEND_URL).toString() : '';
 // const persistedImageUrl = submittedState?.ticket?.public_file_url ? `${BACKEND_URL}${submittedState.ticket.public_file_url}` : submittedState?.ticket?.data;
  const toPublicUrl = (urlOrPath) => {
  if (!urlOrPath) return '';
  try {
    const parsed = new URL(urlOrPath, window.location.origin);
    return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return '';
  }
};

  const publicTicketAbsoluteUrl = toPublicUrl(submittedState?.ticket_public_url);
  const persistedImageUrl = submittedState?.ticket?.public_file_url
  ? toPublicUrl(submittedState.ticket.public_file_url)
  : submittedState?.ticket?.data;
  const handleInputChange = (fieldId, value) => setFormData((current) => ({ ...current, [fieldId]: value }));
  const handleConsentChange = (key, checked) => setConsents((current) => ({ ...current, [key]: checked }));

  const loadPreview = async () => {
    if (!campaign) return;
    setPreviewing(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/public/preview/${campaign.id}`, { data: formData, locale: selectedLocale });
      setPreviewTicket(data.ticket);
    } catch (err) {
      setError(err.response?.data?.detail || (selectedLocale === 'tr' ? 'Önizleme yüklenemedi.' : 'Vorschau konnte nicht geladen werden.'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setShareMessage('');
    try {
      const { data } = await axios.post(`${API}/public/submit/${campaign.id}`, { data: formData, consents, locale: selectedLocale });
      setSubmittedState(data);
    } catch (err) {
      setError(err.response?.data?.detail || (selectedLocale === 'tr' ? 'Bilet oluşturulamadı.' : 'Ticket konnte nicht erstellt werden.'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetFlow = () => {
    const resetData = {};
    campaign?.form_fields?.forEach((field) => {
      resetData[field.id] = field.default_value || (field.type === 'checkbox' || field.type === 'consent' ? false : '');
    });
    setFormData(resetData);
    setConsents(Object.fromEntries(Object.keys(consents).map((key) => [key, false])));
    setPreviewTicket(null);
    setSubmittedState(null);
    setError('');
    setShareMessage('');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDownload = () => {
    if (!persistedImageUrl) return;
    window.open(persistedImageUrl, '_blank', 'noopener,noreferrer');
  };

  const handleNativeShare = async () => {
    if (!submittedState?.ticket_public_url) return;
    const shareTitle = `${campaign?.name} – Hatıra Bileti`;
    const shareText = submittedState?.participant_name
      ? `${submittedState.participant_name} – ${shareTitle}`
      : shareTitle;

    try {
      if (navigator.share && persistedImageUrl) {
        const response = await fetch(persistedImageUrl);
        const blob = await response.blob();
        const file = new File([blob], `${submittedState.serial_number}.png`, { type: 'image/png' });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ title: shareTitle, text: shareText, files: [file], url: publicTicketAbsoluteUrl });
          setShareMessage(selectedLocale === 'tr' ? 'Bilet başarıyla paylaşıldı.' : 'Ticket wurde erfolgreich geteilt.');
          return;
        }

        await navigator.share({ title: shareTitle, text: shareText, url: publicTicketAbsoluteUrl });
        setShareMessage(selectedLocale === 'tr' ? 'Bilet bağlantısı paylaşıldı.' : 'Ticket-Link wurde geteilt.');
        return;
      }

      await navigator.clipboard.writeText(publicTicketAbsoluteUrl);
      setShareMessage(selectedLocale === 'tr' ? 'Bilet bağlantısı kopyalandı.' : 'Ticket-Link wurde kopiert.');
    } catch {
      setShareMessage(selectedLocale === 'tr' ? 'Paylaşım şu anda mümkün değil.' : 'Teilen ist aktuell nicht möglich.');
    }
  };

  const displayParticipantName = submittedState?.participant_name || formData.full_name || formData.name || Object.values(formData).find((value) => typeof value === 'string' && value.trim()) || '—';

  const shareOnPlatform = (platform) => {
    if (!publicTicketAbsoluteUrl) return;
    const text = encodeURIComponent(`${campaign?.name || 'Hatıra Bileti'} – ${submittedState?.participant_name || ''}`.trim());
    const url = encodeURIComponent(publicTicketAbsoluteUrl);
    const targets = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      x: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
    };
    window.open(targets[platform], '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: BRAND.background }}><div className="animate-spin w-8 h-8 border-4 border-[#c4a768] border-t-transparent rounded-full" /></div>;
  }

  if (error && !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: BRAND.background }}>
        <div className="max-w-xl w-full rounded-[28px] border border-white/10 bg-white/5 p-10 text-center text-white backdrop-blur-xl" data-testid="public-campaign-error-state">
          <h1 className="text-3xl font-black tracking-tight">{selectedLocale === 'tr' ? 'Kampanya bulunamadı' : 'Kampagne nicht gefunden'}</h1>
          <p className="mt-4 text-zinc-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ background: `radial-gradient(circle at top, #113945 0%, ${BRAND.background} 55%, #040d12 100%)` }} data-testid="public-campaign-page">
      <div className="relative z-10 px-4 py-10 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl space-y-10">
          <header className="space-y-6">
            <div className="space-y-6">
              <div className="flex justify-end gap-2">
                {supportedLocales.map((locale) => (
                  <button
                    key={locale}
                    type="button"
                    onClick={() => setSelectedLocale(locale)}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold tracking-wide transition-colors ${selectedLocale === locale ? 'border-[#c4a768] bg-[#c4a768] text-[#0b202a]' : 'border-white/15 bg-white/5 text-white'}`}
                    data-testid={`public-locale-switch-${locale}`}
                  >
                    {locale.toUpperCase()}
                  </button>
                ))}
              </div>
              {settings.logo_url && <img src={settings.logo_url} alt="Logo" className="h-20 w-auto" data-testid="public-campaign-logo" />}
              <div className="space-y-4">
                <div className="inline-flex items-center gap-3 rounded-full border border-[#c4a768]/40 bg-white/5 px-4 py-2 text-sm text-[#f3e7ca]">
                  <Ticket className="h-4 w-4" />
                  Hatıra Bileti
                </div>
                <h1 className="font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none text-white">{settings.headline || campaign?.name}</h1>
                <p className="max-w-2xl text-base sm:text-lg text-[#d8dddd] leading-relaxed">{settings.description || t('form_subtitle')}</p>
              </div>

            </div>
          </header>

          <div className="grid gap-8 xl:grid-cols-[minmax(0,1.05fr)_420px] xl:items-start">
            <section ref={formRef} className="rounded-[32px] border border-white/10 bg-[#123a45]/88 shadow-[0_28px_80px_rgba(0,0,0,0.35)] overflow-hidden backdrop-blur-xl" data-testid="public-campaign-form-shell">
              <div className="bg-[#9f1d1e] px-6 py-5 sm:px-8 border-b border-white/10">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#f6d9d9]">Hatıra Bileti</p>
                <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-white">{t('form_title')}</h2>
                <p className="mt-2 text-sm sm:text-base text-[#f7d4d4]">{t('form_subtitle')}</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6 px-6 py-8 sm:px-8" data-testid="public-campaign-form">
                {error && <div className="rounded-[22px] border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100" data-testid="public-campaign-inline-error">{error}</div>}

                {renderedFields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <label className="block text-sm sm:text-base font-semibold tracking-tight text-[#f6f2e8]">
                      {field.label}
                      {field.required && <span className="ml-1 text-[#c4a768]">*</span>}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={formData[field.id] || ''}
                        onChange={(event) => handleInputChange(field.id, event.target.value)}
                        placeholder={field.placeholder || INPUT_HINTS[selectedLocale]?.[field.type] || field.label}
                        required={field.required}
                        rows={4}
                        className="min-h-[132px] w-full rounded-[22px] border border-white/15 bg-white/10 px-5 py-4 text-base text-white outline-none placeholder:text-[#8fa4a9] focus:border-[#c4a768]"
                        data-testid={`field-${field.id}`}
                      />
                    ) : field.type === 'dropdown' ? (
                      <select
                        value={formData[field.id] || ''}
                        onChange={(event) => handleInputChange(field.id, event.target.value)}
                        required={field.required}
                        className="h-16 w-full rounded-[22px] border border-white/15 bg-white/10 px-5 text-base text-white outline-none focus:border-[#c4a768]"
                        data-testid={`field-${field.id}`}
                      >
                        <option value="">{selectedLocale === 'tr' ? 'Seçiniz...' : 'Auswählen...'}</option>
                        {field.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    ) : (
                      <input
                        type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                        value={formData[field.id] || ''}
                        onChange={(event) => handleInputChange(field.id, event.target.value)}
                        placeholder={field.placeholder || INPUT_HINTS[selectedLocale]?.[field.type] || field.label}
                        required={field.required}
                        className="h-16 w-full rounded-[22px] border border-white/15 bg-white/10 px-5 text-base text-white outline-none placeholder:text-[#8fa4a9] focus:border-[#c4a768]"
                        data-testid={`field-${field.id}`}
                      />
                    )}
                    {field.help_text && previewableFieldTypes.has(field.type) && <p className="text-sm text-[#91a6a9]">{field.help_text}</p>}
                  </div>
                ))}

                <div className="space-y-3 rounded-[22px] border border-white/10 bg-black/10 p-5" data-testid="public-consent-section">
                  {Object.entries(consentSettings).filter(([, value]) => value && typeof value === 'object' && value.enabled !== undefined).map(([key, config]) => (
                    config.enabled ? (
                      <label key={key} className="flex items-start gap-3 text-sm sm:text-base text-[#edf1e8]" data-testid={`public-consent-${key}`}>
                        <input type="checkbox" checked={Boolean(consents[key])} onChange={(event) => handleConsentChange(key, event.target.checked)} required={Boolean(config.required)} className="mt-1 h-5 w-5 rounded border-white/20 bg-transparent text-[#c4a768]" />
                        <span>
                          {config[`label_${selectedLocale}`] || config.label_de}
                          {config.url && (
                            <a href={config.url} target="_blank" rel="noopener noreferrer" className="ml-1 underline text-[#f3d18e]" data-testid={`public-consent-link-${key}`}>
                              {selectedLocale === 'tr' ? 'Detaylar' : 'Details'}
                            </a>
                          )}
                        </span>
                      </label>
                    ) : null
                  ))}
                </div>

                <div className="flex flex-col gap-3 lg:flex-row">
                  {canPreview && (
                    <button type="button" onClick={loadPreview} disabled={previewing} className="inline-flex h-16 flex-1 items-center justify-center gap-3 rounded-[22px] border border-white/15 bg-white/5 px-5 text-base font-semibold text-white transition-colors hover:bg-white/10 disabled:opacity-60" data-testid="preview-ticket-btn">
                      <Ticket className="w-5 h-5" />
                      {previewing ? (selectedLocale === 'tr' ? 'Önizleme yükleniyor...' : 'Vorschau lädt...') : t('preview_ticket')}
                    </button>
                  )}
                  <button type="submit" disabled={submitting} className="inline-flex h-16 flex-1 items-center justify-center gap-3 rounded-[22px] px-5 text-base font-semibold text-white transition-colors disabled:opacity-60" style={{ backgroundColor: BRAND.accent }} data-testid="submit-form-btn">
                    <Ticket className="w-5 h-5" />
                    {submitting ? (selectedLocale === 'tr' ? 'Bilet oluşturuluyor...' : 'Ticket wird erstellt...') : t('create_ticket')}
                  </button>
                </div>
              </form>
            </section>

            <aside className="rounded-[32px] border border-white/10 bg-[#102d38]/88 p-6 shadow-[0_28px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl" data-testid="public-ticket-preview-panel">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#c4a768]">{t('preview_title')}</p>
                  <h2 className="mt-3 text-4xl font-black tracking-tight text-white">{t('preview_title')}</h2>
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-[#d8dddd]">{campaign?.generate_ticket ? 'PNG' : 'Versteckt'}</div>
              </div>

              <div className="mt-6 overflow-hidden rounded-[26px] border border-white/10 bg-black/20 p-4 min-h-[360px]">
                {previewTicket ? (
                  <img src={previewTicket.data} alt="Ticket Vorschau" className="h-full w-full rounded-[20px] object-cover" data-testid="public-ticket-preview-image" />
                ) : (
                  <div className="flex h-full min-h-[320px] items-center justify-center rounded-[20px] border border-dashed border-white/10 bg-white/[0.03] px-8 text-center text-[#b7c5c5]" data-testid="public-ticket-preview-empty">
                    {selectedLocale === 'tr' ? 'Biletinizin nasıl görüneceğini görmek için önce önizleme alın.' : 'Lade zuerst die Vorschau, damit du das finale Ticket vor dem Erstellen sehen kannst.'}
                  </div>
                )}
              </div>

              <p className="mt-5 text-sm leading-relaxed text-[#b7c5c5]">
                {previewTicket
                  ? (selectedLocale === 'tr' ? 'Bu önizleme, gönderim öncesi final yerleşimi gösterir.' : 'Diese Vorschau zeigt das finale Layout vor dem Erstellen.')
                  : (selectedLocale === 'tr' ? 'Form alanlarınızı doldurun ve ardından görsel önizlemeyi inceleyin.' : 'Fülle deine Daten aus und prüfe anschließend die visuelle Vorschau.')}
              </p>
            </aside>
          </div>

          {submittedState && (
            <section ref={resultRef} className="grid gap-6 xl:grid-cols-[220px_minmax(0,1fr)_280px] rounded-[34px] border border-[#c4a768]/20 bg-[#102e38]/92 p-6 shadow-[0_32px_90px_rgba(0,0,0,0.4)] backdrop-blur-xl" data-testid="public-campaign-success-state">
              <div className="overflow-hidden rounded-[26px] border border-white/10 bg-black/20 min-h-[240px]">
                {persistedImageUrl ? (
                  <img src={persistedImageUrl} alt="Ticket Vorschau" className="h-full w-full object-cover" data-testid="public-generated-ticket-image" />
                ) : (
                  <div className="flex h-full min-h-[220px] items-center justify-center text-center text-[#b7c5c5]" data-testid="public-generated-ticket-pending-state">
                    {selectedLocale === 'tr' ? 'Bilet onay bekliyor.' : 'Ticket wartet auf Freigabe.'}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="inline-flex items-center gap-3 rounded-full bg-[#dff0cf] px-4 py-2 text-sm font-semibold text-[#173b25]" data-testid="public-success-badge">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#2d6a3a] text-white">✓</span>
                  {t('success_title')}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#c4a768]">{t('result_title')}</p>
                  <h2 className="mt-2 text-4xl font-black tracking-tight text-white" data-testid="public-success-title">{t('success_title')}</h2>
                </div>
                <p className="max-w-xl text-base leading-relaxed text-[#d6dfdf]">{t('success_text')}</p>
                <div className="grid gap-3 rounded-[24px] border border-white/10 bg-white/[0.04] p-5 text-sm text-[#d8dddd]">
                  <p><span className="text-[#8fa4a9]">İsim:</span> <span className="font-semibold text-white">{displayParticipantName}</span></p>
                  <p><span className="text-[#8fa4a9]">Seri No:</span> <span className="font-semibold text-white">{submittedState.serial_number}</span></p>
                  <p><span className="text-[#8fa4a9]">Durum:</span> <span className="font-semibold text-white">{PUBLIC_STATUS_LABELS[submittedState.ticket_status] || submittedState.ticket_status || '—'}</span></p>
                </div>
              </div>

              <div className="space-y-4">
                <button onClick={handleDownload} disabled={!persistedImageUrl} className="inline-flex h-14 w-full items-center justify-center gap-3 rounded-[20px] px-5 text-base font-semibold text-white transition-colors disabled:opacity-50" style={{ backgroundColor: BRAND.accent }} data-testid="download-ticket-btn">
                  <Download className="w-5 h-5" />
                  {t('download_ticket')}
                </button>
                <button onClick={handleNativeShare} disabled={!submittedState.ticket_public_url} className="inline-flex h-14 w-full items-center justify-center gap-3 rounded-[20px] border border-white/10 bg-white/5 px-5 text-base font-semibold text-white transition-colors hover:bg-white/10 disabled:opacity-50" data-testid="share-ticket-btn">
                  <ShareNetwork className="w-5 h-5" />
                  {t('share_ticket')}
                </button>
                <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4" data-testid="social-share-logos-panel">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#c4a768] mb-3">Social Media</p>
                  <div className="flex flex-wrap gap-3">
                    <button type="button" onClick={() => shareOnPlatform('whatsapp')} className={`${socialButtonBase} bg-[#25D366]/20 hover:bg-[#25D366]/30`} data-testid="share-whatsapp-button"><FaWhatsapp className="h-5 w-5" /></button>
                    <button type="button" onClick={() => shareOnPlatform('facebook')} className={`${socialButtonBase} bg-[#1877F2]/20 hover:bg-[#1877F2]/30`} data-testid="share-facebook-button"><FaFacebookF className="h-5 w-5" /></button>
                    <button type="button" onClick={() => shareOnPlatform('x')} className={`${socialButtonBase} bg-white/10 hover:bg-white/20`} data-testid="share-x-button"><FaXTwitter className="h-5 w-5" /></button>
                    <button type="button" onClick={() => shareOnPlatform('telegram')} className={`${socialButtonBase} bg-[#229ED9]/20 hover:bg-[#229ED9]/30`} data-testid="share-telegram-button"><FaTelegram className="h-5 w-5" /></button>
                  </div>
                </div>
                {shareMessage && <p className="rounded-[20px] border border-white/10 bg-white/[0.04] p-4 text-sm text-[#d6dfdf]" data-testid="share-feedback-message">{shareMessage}</p>}
                <button onClick={resetFlow} className="inline-flex h-14 w-full items-center justify-center gap-3 rounded-[20px] border border-white/10 bg-white/[0.04] px-5 text-base font-semibold text-white transition-colors hover:bg-white/[0.08]" data-testid="reset-ticket-flow-btn">
                  <ArrowClockwise className="w-5 h-5" />
                  {t('reset_form')}
                </button>
              </div>
            </section>
          )}

          <footer className="grid gap-4 sm:grid-cols-3 rounded-[28px] border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md" data-testid="public-legal-info-footer">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#c4a768]">{t('responsible_label')}</p>
              <p className="mt-2 text-sm text-white">{legalInfo.organization_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#c4a768]">Kontakt</p>
              <p className="mt-2 text-sm text-white">{legalInfo.contact_email || '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#c4a768]">Hinweis</p>
              <p className="mt-2 text-sm text-white leading-relaxed">{selectedLocale === 'tr' ? (consentSettings.purpose_text_tr || FALLBACK_TRANSLATIONS.tr.success_text) : (consentSettings.purpose_text_de || FALLBACK_TRANSLATIONS.de.success_text)}</p>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
